import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Check, Pencil, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatWhatsApp } from '@/frontend/utils/whatsappMask'

interface AllowlistData {
  entries: string[]
  dm_policy: string | null
  entry_names?: Record<string, string | null>
}

interface Props {
  allowlist: AllowlistData | null
  telegramAllowlist: AllowlistData | null
  hasWhatsApp: boolean
  hasTelegram: boolean
  basePath: string
  readOnly?: boolean
}

function AllowlistSection({
  title,
  data,
  channel,
  basePath,
  formatEntry,
  placeholder,
  namePlaceholder,
  emptyLabel,
  supportsNames = false,
  readOnly = false,
}: {
  title: string
  data: AllowlistData | null
  channel: string
  basePath: string
  formatEntry: (entry: string) => string
  placeholder: string
  namePlaceholder?: string
  emptyLabel: string
  supportsNames?: boolean
  readOnly?: boolean
}) {
  const [newEntry, setNewEntry] = useState('')
  const [newName, setNewName] = useState('')
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<string | null>(null)
  const [editingEntry, setEditingEntry] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [updatingName, setUpdatingName] = useState(false)
  const [changingPolicy, setChangingPolicy] = useState(false)
  const visibleEntries = data?.entries.filter((entry) => entry.trim()) ?? []

  const handleSetPolicy = (policy: string) => {
    setChangingPolicy(true)
    router.post(`${basePath}/set_dm_policy`, { dm_policy: policy, channel }, {
      preserveScroll: true,
      onFinish: () => setChangingPolicy(false),
    })
  }

  const handleAdd = () => {
    if (!newEntry.trim()) return
    setAdding(true)
    router.post(`${basePath}/add_to_allowlist`, {
      phone_number: newEntry,
      channel,
      ...(supportsNames ? { display_name: newName } : {}),
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setNewEntry('')
        setNewName('')
      },
      onFinish: () => setAdding(false),
    })
  }

  const handleRemove = (entry: string) => {
    setRemoving(entry)
    router.post(`${basePath}/remove_from_allowlist`, { phone_number: entry, channel }, {
      preserveScroll: true,
      onFinish: () => setRemoving(null),
    })
  }

  const startEditingName = (entry: string) => {
    setEditingEntry(entry)
    setEditingName(data?.entry_names?.[entry] ?? '')
  }

  const cancelEditingName = () => {
    setEditingEntry(null)
    setEditingName('')
  }

  const handleUpdateName = (entry: string) => {
    setUpdatingName(true)
    router.patch(`${basePath}/update_allowlist_entry`, {
      phone_number: entry,
      display_name: editingName,
    }, {
      preserveScroll: true,
      onSuccess: cancelEditingName,
      onFinish: () => setUpdatingName(false),
    })
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6 space-y-4">
      <h2 className="text-[14px] font-semibold text-foreground">{title}</h2>
      {data ? (
        <div className="space-y-4">
          {readOnly ? (
            <div className="pb-4 border-b border-border">
              <p className="text-sm text-muted-foreground">
                Política: {data.dm_policy === 'open' ? 'Aberta (qualquer pessoa)' : 'Allowlist (somente autorizados)'}
              </p>
            </div>
          ) : (
          <div className="flex gap-4 pb-4 border-b border-border">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={`${channel}_dm_policy`}
                value="open"
                checked={data.dm_policy === 'open'}
                onChange={() => handleSetPolicy('open')}
                disabled={changingPolicy}
                className="accent-brand"
              />
              <span className="text-sm text-foreground">Aberta (qualquer pessoa)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={`${channel}_dm_policy`}
                value="allowlist"
                checked={data.dm_policy === 'allowlist'}
                onChange={() => handleSetPolicy('allowlist')}
                disabled={changingPolicy}
                className="accent-brand"
              />
              <span className="text-sm text-foreground">Allowlist (somente autorizados)</span>
            </label>
          </div>
          )}
          {data.dm_policy === 'allowlist' && (
            <>
              {visibleEntries.length > 0 ? (
                <div className="space-y-2">
                  {visibleEntries.map((entry) => {
                    const displayName = data.entry_names?.[entry]?.trim() ?? ''
                    const isEditing = editingEntry === entry

                    return (
                      <div key={entry} className="flex flex-col gap-3 rounded-lg bg-muted px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                        {isEditing && supportsNames ? (
                          <div className="grid flex-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                            <div className="grid gap-1">
                              <span className="text-xs text-muted-foreground font-mono">{formatEntry(entry)}</span>
                              <input
                                type="text"
                                value={editingName}
                                onChange={(event) => setEditingName(event.target.value)}
                                placeholder={namePlaceholder}
                                onKeyDown={(event) => { if (event.key === 'Enter') handleUpdateName(entry) }}
                                className="w-full min-w-0 text-sm border border-border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-ring/30"
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Salvar nome"
                                disabled={updatingName}
                                onClick={() => handleUpdateName(entry)}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Cancelar edição"
                                disabled={updatingName}
                                onClick={cancelEditingName}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="min-w-0">
                              {displayName ? (
                                <>
                                  <p className="truncate text-sm font-medium text-foreground">{displayName}</p>
                                  <p className="text-xs text-muted-foreground font-mono">{formatEntry(entry)}</p>
                                </>
                              ) : (
                                <>
                                  <p className="text-sm text-foreground font-mono">{formatEntry(entry)}</p>
                                  {supportsNames && <p className="text-xs text-muted-foreground">Sem nome</p>}
                                </>
                              )}
                            </div>
                            {!readOnly && (
                              <div className="flex shrink-0 items-center gap-1">
                                {supportsNames && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label="Editar nome"
                                    disabled={removing !== null || editingEntry !== null}
                                    onClick={() => startEditingName(entry)}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                )}
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 text-xs text-destructive transition-opacity duration-150 hover:opacity-80 disabled:opacity-50"
                                  disabled={removing !== null || editingEntry !== null}
                                  onClick={() => handleRemove(entry)}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  {removing === entry ? 'Removendo…' : 'Remover'}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{emptyLabel}</p>
              )}
              {!readOnly && (
              <div className={`grid gap-2 pt-2 border-t border-border ${supportsNames ? 'md:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_auto]' : 'grid-cols-[minmax(0,1fr)_auto]'}`}>
                <input
                  type="text"
                  value={newEntry}
                  onChange={(e) => setNewEntry(e.target.value)}
                  placeholder={placeholder}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAdd() }}
                  className="w-full min-w-0 text-sm border border-border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-ring/30"
                />
                {supportsNames && (
                  <input
                    type="text"
                    value={newName}
                    onChange={(event) => setNewName(event.target.value)}
                    placeholder={namePlaceholder}
                    onKeyDown={(event) => { if (event.key === 'Enter') handleAdd() }}
                    className="w-full min-w-0 text-sm border border-border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-ring/30"
                  />
                )}
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAdd}
                  disabled={adding || !newEntry.trim()}
                >
                  {adding ? 'Adicionando…' : 'Adicionar'}
                </Button>
              </div>
              )}
            </>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Não foi possível carregar a allowlist.</p>
      )}
    </div>
  )
}

const AllowlistTab: React.FC<Props> = ({ allowlist, telegramAllowlist, hasWhatsApp, hasTelegram, basePath, readOnly = false }) => {
  if (!hasWhatsApp && !hasTelegram) {
    // Estado vazio do DS: uma frase só, média — sem caixa.
    return <p className="py-2 text-[13px] text-muted-foreground">Conecte um canal primeiro pra liberar a allowlist.</p>
  }

  return (
    <div className="space-y-6">
      {hasWhatsApp && (
        <AllowlistSection
          title="Allowlist WhatsApp"
          data={allowlist}
          channel="whatsapp-cloud"
          basePath={basePath}
          formatEntry={formatWhatsApp}
          placeholder="+55 11 99999-9999"
          namePlaceholder="Nome"
          emptyLabel="Nenhum número liberado ainda. Adicione o primeiro abaixo."
          supportsNames
          readOnly={readOnly}
        />
      )}
      {hasTelegram && (
        <AllowlistSection
          title="Allowlist Telegram"
          data={telegramAllowlist}
          channel="telegram"
          basePath={basePath}
          formatEntry={(entry) => entry}
          placeholder="ID numérico do Telegram"
          emptyLabel="Nenhum ID liberado ainda. Adicione o primeiro abaixo."
          readOnly={readOnly}
        />
      )}
    </div>
  )
}

export default AllowlistTab
